/**
 * Centralized toast message constants for consistent UX copy across the app.
 * Import these instead of writing inline toast messages.
 */

export const TOAST = {
  // Generic success/error
  success: {
    title: 'Success',
    description: 'Operation completed successfully',
  },
  error: {
    title: 'Error',
    description: 'Something went wrong. Please try again.',
  },

  // Save operations
  saved: {
    title: 'Saved',
    description: 'Your changes have been saved',
  },
  saveFailed: {
    title: 'Save failed',
    description: 'Unable to save your changes. Please try again.',
  },

  // Delete operations
  deleted: (item = 'Item') => ({
    title: 'Deleted',
    description: `${item} has been removed`,
  }),
  deleteFailed: (item = 'item') => ({
    title: 'Delete failed',
    description: `Unable to delete ${item}. Please try again.`,
  }),

  // Create operations
  created: (item = 'Item') => ({
    title: 'Created',
    description: `${item} has been created`,
  }),
  createFailed: (item = 'item') => ({
    title: 'Creation failed',
    description: `Unable to create ${item}. Please try again.`,
  }),

  // Update operations
  updated: (item = 'Item') => ({
    title: 'Updated',
    description: `${item} has been updated`,
  }),
  updateFailed: (item = 'item') => ({
    title: 'Update failed',
    description: `Unable to update ${item}. Please try again.`,
  }),

  // Copy operations
  copied: (item = 'Content') => ({
    title: 'Copied',
    description: `${item} copied to clipboard`,
  }),
  copyFailed: {
    title: 'Copy failed',
    description: 'Unable to copy to clipboard',
  },

  // Share operations
  shared: (count = 1) => ({
    title: 'Shared',
    description: `${count} item${count > 1 ? 's' : ''} shared with project`,
  }),
  shareFailed: {
    title: 'Share failed',
    description: 'Failed to share some items',
  },
  unshared: (count = 1) => ({
    title: 'Removed',
    description: `${count} item${count > 1 ? 's' : ''} removed from project`,
  }),

  // Network/connectivity
  offline: {
    title: "You're offline",
    description: 'Changes will sync when you reconnect',
  },
  backOnline: {
    title: "You're back online",
    description: 'Your changes will now sync',
  },

  // Authentication
  loginSuccess: {
    title: 'Welcome back',
    description: 'You have been logged in successfully',
  },
  loginFailed: {
    title: 'Login failed',
    description: 'Invalid email or password',
  },
  logoutSuccess: {
    title: 'Logged out',
    description: 'You have been logged out successfully',
  },
  signupSuccess: {
    title: 'Account created',
    description: 'Please check your email to verify your account',
  },
  passwordResetSent: {
    title: 'Reset email sent',
    description: 'Check your email for password reset instructions',
  },
  passwordUpdated: {
    title: 'Password updated',
    description: 'Your password has been changed successfully',
  },

  // Rate limiting
  rateLimited: (resetTime?: string) => ({
    title: 'Rate limited',
    description: resetTime
      ? `Too many requests. Try again ${resetTime}`
      : 'Too many requests. Please wait and try again.',
  }),

  // AI operations
  aiError: {
    title: 'AI Error',
    description: 'Unable to process your request. Please try again.',
  },
  aiGenerating: {
    title: 'Generating',
    description: 'AI is processing your request...',
  },

  // Export operations
  exportSuccess: (format: string) => ({
    title: 'Export complete',
    description: `Your ${format} has been downloaded`,
  }),
  exportFailed: {
    title: 'Export failed',
    description: 'Unable to export. Please try again.',
  },

  // Research operations
  researchAdded: {
    title: 'Added to research',
    description: 'Item has been added to your library',
  },
  embeddingComplete: {
    title: 'Processing complete',
    description: 'Your research is now searchable by AI',
  },

  // Pipeline operations
  pipelineStarted: {
    title: 'Pipeline started',
    description: 'Your writing pipeline is now running',
  },
  pipelineComplete: {
    title: 'Pipeline complete',
    description: 'All steps have been processed',
  },
  pipelinePaused: {
    title: 'Pipeline paused',
    description: 'Waiting for your review',
  },

  // Tutorial/onboarding
  tourComplete: {
    title: 'Tour complete!',
    description: 'You can replay anytime via the ? icon',
  },
  tourSkipped: {
    title: 'Tour skipped',
    description: 'Click the ? icon in the header anytime to replay',
  },

  // Validation
  validationError: (message: string) => ({
    title: 'Validation error',
    description: message,
  }),
  requiredField: (field: string) => ({
    title: 'Required field',
    description: `${field} is required`,
  }),

  // ============================================
  // NESTED DOMAIN-SPECIFIC MESSAGES
  // ============================================

  AUTH: {
    UNAUTHORIZED: {
      title: 'Unauthorized',
      description: 'You must be logged in to perform this action',
    },
    LOGIN_SUCCESS: {
      title: 'Welcome back',
      description: 'You have been logged in successfully',
    },
    LOGIN_ERROR: {
      title: 'Login failed',
      description: 'Invalid email or password',
    },
    LOGOUT_SUCCESS: {
      title: 'Logged out',
      description: 'You have been logged out successfully',
    },
    SIGNUP_SUCCESS: {
      title: 'Account created',
      description: 'Please check your email to verify your account',
    },
    PASSWORD_RESET_SENT: {
      title: 'Reset email sent',
      description: 'Check your email for password reset instructions',
    },
    PASSWORD_UPDATED: {
      title: 'Password updated',
      description: 'Your password has been changed successfully',
    },
    SESSION_EXPIRED: {
      title: 'Session expired',
      description: 'Please log in again to continue',
    },
  },

  PROJECT: {
    CREATE_SUCCESS: {
      title: 'Project created',
      description: 'Your new project is ready',
    },
    CREATE_ERROR: {
      title: 'Creation failed',
      description: 'Unable to create project. Please try again.',
    },
    UPDATE_SUCCESS: {
      title: 'Project updated',
      description: 'Your changes have been saved',
    },
    UPDATE_ERROR: {
      title: 'Update failed',
      description: 'Unable to update project. Please try again.',
    },
    DELETE_SUCCESS: {
      title: 'Project deleted',
      description: 'Project has been removed',
    },
    DELETE_ERROR: {
      title: 'Delete failed',
      description: 'Unable to delete project. Please try again.',
    },
    LOAD_ERROR: {
      title: 'Load failed',
      description: 'Unable to load project. Please try again.',
    },
  },

  CHAPTER: {
    ADD_SUCCESS: {
      title: 'Chapter added',
      description: 'New chapter has been created',
    },
    ADD_ERROR: {
      title: 'Add failed',
      description: 'Unable to add chapter. Please try again.',
    },
    UPDATE_SUCCESS: {
      title: 'Chapter updated',
      description: 'Your changes have been saved',
    },
    UPDATE_ERROR: {
      title: 'Update failed',
      description: 'Unable to update chapter. Please try again.',
    },
    DELETE_SUCCESS: {
      title: 'Chapter deleted',
      description: 'Chapter has been removed',
    },
    DELETE_ERROR: {
      title: 'Delete failed',
      description: 'Unable to delete chapter. Please try again.',
    },
    REORDER_ERROR: {
      title: 'Reorder failed',
      description: 'Unable to reorder chapters. Please try again.',
    },
  },

  SECTION: {
    ADD_SUCCESS: {
      title: 'Section added',
      description: 'New section has been created',
    },
    ADD_ERROR: {
      title: 'Add failed',
      description: 'Unable to add section. Please try again.',
    },
    UPDATE_SUCCESS: {
      title: 'Section updated',
      description: 'Your changes have been saved',
    },
    UPDATE_ERROR: {
      title: 'Update failed',
      description: 'Unable to update section. Please try again.',
    },
    DELETE_SUCCESS: {
      title: 'Section deleted',
      description: 'Section has been removed',
    },
    DELETE_ERROR: {
      title: 'Delete failed',
      description: 'Unable to delete section. Please try again.',
    },
    REORDER_ERROR: {
      title: 'Reorder failed',
      description: 'Unable to reorder sections. Please try again.',
    },
  },

  RESEARCH: {
    ADD_SUCCESS: {
      title: 'Added to research',
      description: 'Item has been added to your library',
    },
    ADD_ERROR: {
      title: 'Add failed',
      description: 'Unable to add research item. Please try again.',
    },
    UPDATE_SUCCESS: {
      title: 'Research updated',
      description: 'Your changes have been saved',
    },
    UPDATE_ERROR: {
      title: 'Update failed',
      description: 'Unable to update research item. Please try again.',
    },
    DELETE_SUCCESS: {
      title: 'Research deleted',
      description: 'Item has been removed from your library',
    },
    DELETE_ERROR: {
      title: 'Delete failed',
      description: 'Unable to delete research item. Please try again.',
    },
    BULK_DELETE_SUCCESS: (count: number) => ({
      title: 'Items deleted',
      description: `${count} item${count > 1 ? 's' : ''} removed from library`,
    }),
    BULK_DELETE_ERROR: {
      title: 'Delete failed',
      description: 'Unable to delete some items. Please try again.',
    },
    SHARE_SUCCESS: (count: number) => ({
      title: 'Shared',
      description: `${count} item${count > 1 ? 's' : ''} shared with project`,
    }),
    SHARE_ERROR: {
      title: 'Share failed',
      description: 'Failed to share some items',
    },
    UNSHARE_SUCCESS: (count: number) => ({
      title: 'Removed',
      description: `${count} item${count > 1 ? 's' : ''} removed from project`,
    }),
    UNSHARE_ERROR: {
      title: 'Remove failed',
      description: 'Failed to remove some items from project',
    },
    EMBED_SUCCESS: {
      title: 'Processing complete',
      description: 'Your research is now searchable by AI',
    },
    EMBED_ERROR: {
      title: 'Processing failed',
      description: 'Unable to process research for AI search',
    },
    FETCH_SUCCESS: {
      title: 'Content fetched',
      description: 'Web content has been added to your library',
    },
    FETCH_ERROR: {
      title: 'Fetch failed',
      description: 'Unable to fetch web content. Please try again.',
    },
    UPLOAD_SUCCESS: {
      title: 'Upload complete',
      description: 'File has been added to your library',
    },
    UPLOAD_ERROR: {
      title: 'Upload failed',
      description: 'Unable to upload file. Please try again.',
    },
    LOAD_ERROR: {
      title: 'Load failed',
      description: 'Failed to load research items. Please try again.',
    },
  },

  TUTORIAL: {
    COMPLETE: {
      title: 'Tour complete!',
      description: 'You can replay anytime via the ? icon',
    },
    SKIPPED: {
      title: 'Tour skipped',
      description: 'Click the ? icon in the header anytime to replay',
    },
  },

  CITATION: {
    ADD_SUCCESS: {
      title: 'Citation added',
      description: 'Citation has been recorded',
    },
    ADD_ERROR: {
      title: 'Add failed',
      description: 'Unable to add citation. Please try again.',
    },
    UPDATE_SUCCESS: {
      title: 'Citation updated',
      description: 'Your changes have been saved',
    },
    UPDATE_ERROR: {
      title: 'Update failed',
      description: 'Unable to update citation. Please try again.',
    },
    DELETE_SUCCESS: {
      title: 'Citation removed',
      description: 'Citation has been removed',
    },
    DELETE_ERROR: {
      title: 'Delete failed',
      description: 'Unable to remove citation. Please try again.',
    },
    COPY_SUCCESS: {
      title: 'Copied',
      description: 'Bibliography copied to clipboard',
    },
  },

  SETTINGS: {
    SAVE_SUCCESS: {
      title: 'Settings saved',
      description: 'Your preferences have been updated',
    },
    SAVE_ERROR: {
      title: 'Save failed',
      description: 'Unable to save settings. Please try again.',
    },
    LOAD_ERROR: {
      title: 'Load failed',
      description: 'Unable to load settings. Please try again.',
    },
  },

  API_KEY: {
    SAVE_SUCCESS: {
      title: 'API key saved',
      description: 'Your API key has been stored securely',
    },
    SAVE_ERROR: {
      title: 'Save failed',
      description: 'Unable to save API key. Please try again.',
    },
    DELETE_SUCCESS: {
      title: 'API key removed',
      description: 'Your API key has been deleted',
    },
    DELETE_ERROR: {
      title: 'Delete failed',
      description: 'Unable to remove API key. Please try again.',
    },
    TEST_SUCCESS: {
      title: 'Connection successful',
      description: 'API key is valid and working',
    },
    TEST_ERROR: {
      title: 'Connection failed',
      description: 'API key is invalid or expired',
    },
  },

  PIPELINE: {
    START_SUCCESS: {
      title: 'Pipeline started',
      description: 'Your writing pipeline is now running',
    },
    START_ERROR: {
      title: 'Start failed',
      description: 'Unable to start pipeline. Please try again.',
    },
    COMPLETE: {
      title: 'Pipeline complete',
      description: 'All steps have been processed',
    },
    PAUSED: {
      title: 'Pipeline paused',
      description: 'Waiting for your review',
    },
    STEP_COMPLETE: (step: string) => ({
      title: 'Step complete',
      description: `${step} has been generated`,
    }),
    STEP_ERROR: (step: string) => ({
      title: 'Step failed',
      description: `Unable to generate ${step}. Please try again.`,
    }),
    FEEDBACK_REQUIRED: {
      title: 'Please provide feedback for regeneration',
    },
    REGENERATING: {
      title: 'Regenerating with your feedback...',
    },
    SKIPPED: {
      title: 'Step skipped',
    },
    DELETED: {
      title: 'Pipeline run deleted',
    },
    DELETE_ERROR: {
      title: 'Failed to delete pipeline run',
    },
    RESTORED: {
      title: 'Pipeline restored',
    },
    RATE_LIMIT: (seconds: number) => ({
      title: 'Rate limit exceeded',
      description: `Please wait ${seconds} seconds.`,
    }),
  },

  EXPORT: {
    SUCCESS: (format: string) => ({
      title: 'Export complete',
      description: `Your ${format} has been downloaded`,
    }),
    ERROR: {
      title: 'Export failed',
      description: 'Unable to export. Please try again.',
    },
    PREPARING: {
      title: 'Preparing export',
      description: 'Your file is being generated...',
    },
    NO_CHAPTERS: {
      title: 'No chapters to export',
      description: 'Create some chapters first.',
    },
  },

  BULK: {
    SELECT_ALL: (count: number) => ({
      title: 'Selected',
      description: `${count} items selected`,
    }),
    CLEAR_SELECTION: {
      title: 'Cleared',
      description: 'Selection cleared',
    },
  },

  WRITING_GOAL: {
    UPDATE_SUCCESS: {
      title: 'Goal updated',
      description: 'Daily writing goal has been updated',
    },
    UPDATE_ERROR: {
      title: 'Update failed',
      description: 'Failed to update daily goal. Please try again.',
    },
    GOAL_MET: {
      title: 'Goal achieved!',
      description: 'You met your daily writing goal',
    },
    STREAK: (days: number) => ({
      title: `${days} day streak!`,
      description: 'Keep up the great work',
    }),
    SESSION_ERROR: {
      title: 'Recording failed',
      description: 'Failed to record writing session. Please try again.',
    },
  },

  ADMIN: {
    SETTINGS_SAVED: {
      title: 'Settings saved',
      description: 'Platform settings have been updated',
    },
    SETTINGS_ERROR: {
      title: 'Save failed',
      description: 'Unable to save platform settings',
    },
    NO_CHANGES: {
      title: 'No changes to save',
    },
    USER_LOAD_ERROR: {
      title: 'Load failed',
      description: 'Unable to load users',
    },
    ROLE_GRANTED: (role: string) => ({
      title: 'Role granted',
      description: `Successfully granted ${role} role`,
    }),
    ROLE_REVOKED: (role: string) => ({
      title: 'Role revoked',
      description: `Successfully revoked ${role} role`,
    }),
    ROLE_ERROR: {
      title: 'Update failed',
      description: 'Unable to update user role',
    },
    RATE_LIMIT_REMOVED: {
      title: 'Rate limit removed',
      description: 'User now has unlimited AI requests',
    },
    RATE_LIMIT_APPLIED: {
      title: 'Rate limit applied',
      description: 'User is now subject to rate limits',
    },
    DETECTION_COMPLETE: (count: number) => ({
      title: `Detection complete. Found ${count} new alert(s).`,
    }),
    AUDIT_LOGS_LOAD_ERROR: {
      title: 'Failed to load audit logs',
      description: 'Please try again.',
    },
    MODEL_COST: {
      UPDATE_SUCCESS: { title: 'Cost updated successfully' },
      UPDATE_ERROR: { title: 'Failed to update cost' },
      ADD_SUCCESS: { title: 'Model cost added successfully' },
      ADD_ERROR: { title: 'Failed to add model cost' },
      DELETE_SUCCESS: { title: 'Model cost deleted' },
      DELETE_ERROR: { title: 'Failed to delete' },
      VALIDATION_ERROR: { title: 'Provider and model are required' },
    },
  },

  DRAFT: {
    SYNC_SUCCESS: {
      title: 'Draft synced',
      description: 'Your offline changes have been saved',
    },
    SYNC_ERROR: {
      title: 'Sync failed',
      description: 'Your draft is still saved locally. We\'ll try again later.',
    },
    SAVED_LOCALLY: {
      title: 'Saved locally',
      description: 'Changes saved offline, will sync when connected',
    },
  },

  ACCOUNT: {
    PASSWORD_MISMATCH: {
      title: 'Error',
      description: 'Passwords do not match',
    },
    PASSWORD_TOO_SHORT: {
      title: 'Error',
      description: 'Password must be at least 6 characters',
    },
    PASSWORD_SUCCESS: {
      title: 'Success',
      description: 'Password updated successfully',
    },
    PASSWORD_ERROR: {
      title: 'Error',
      description: 'Failed to update password',
    },
    EMAIL_SUCCESS: {
      title: 'Verification sent',
      description: 'Please check your new email for a verification link',
    },
    EMAIL_ERROR: {
      title: 'Error',
      description: 'Failed to update email',
    },
    DELETE_SUCCESS: {
      title: 'Account deleted',
      description: 'Your account has been permanently deleted',
    },
    DELETE_ERROR: {
      title: 'Error',
      description: 'Failed to delete account. Please try again.',
    },
    REGISTRATION_DISABLED: {
      title: 'Registration disabled',
      description: 'New account registration is currently closed',
    },
    ACCOUNT_EXISTS: {
      title: 'Account exists',
      description: 'This email is already registered. Please log in instead.',
    },
    WELCOME: {
      title: 'Account created!',
      description: 'Welcome to AtlasDraft. You can now start creating.',
    },
    RESET_SUCCESS: {
      title: 'Check your email',
      description: 'We sent you a password reset link. Check your inbox.',
    },
    RESET_ERROR: {
      title: 'Reset failed',
      description: 'Unable to send password reset email',
    },
  },

  NETWORK: {
    OFFLINE: {
      title: "You're offline",
      description: 'Changes will sync when you reconnect',
    },
    BACK_ONLINE: {
      title: "You're back online",
      description: 'Your changes will now sync',
    },
  },

  ACHIEVEMENT: {
    UNLOCKED: (name: string, icon: string) => ({
      title: `Achievement Unlocked: ${name}! ${icon}`,
    }),
  },

  AI: {
    INPUT_REQUIRED: {
      title: 'Please enter some content',
    },
    GENERATED: {
      title: 'Generated successfully',
    },
    GENERATE_ERROR: {
      title: 'Failed to generate content. Please try again.',
    },
    COPIED: {
      title: 'Copied to clipboard',
    },
    RATE_LIMIT: (seconds: number) => ({
      title: 'Rate limit exceeded',
      description: `Please wait ${seconds} seconds.`,
    }),
  },

  EMAIL: {
    VERIFICATION_SENT: {
      title: 'Verification email sent! Check your inbox.',
    },
    VERIFICATION_ERROR: (message?: string) => ({
      title: message || 'Failed to send verification email',
    }),
  },

  SAVE_PIPELINE: {
    NO_OUTLINE: {
      title: 'No outline content to save',
    },
    PARSE_ERROR: {
      title: 'Could not parse chapters from outline',
    },
    CHAPTERS_CREATED: (count: number) => ({
      title: `Created ${count} chapters from outline`,
    }),
    SAVE_CHAPTERS_ERROR: {
      title: 'Failed to save chapters',
    },
    NO_CHAPTER_CONTENT: {
      title: 'No chapter content to save',
    },
    NO_CHAPTERS_FOUND: {
      title: 'No chapters found. Please save the outline first.',
    },
    SAVE_SECTIONS_ERROR: {
      title: 'Failed to save sections',
    },
    NO_DRAFT: {
      title: 'No draft content to save',
    },
    NO_CHAPTERS: {
      title: 'No chapters found',
    },
    SAVE_DRAFT_ERROR: {
      title: 'Failed to save draft content',
    },
    NO_REVISION: {
      title: 'No revision content to save',
    },
    SAVE_REVISION_ERROR: {
      title: 'Failed to save revision content',
    },
    SECTIONS_UPDATED: (count: number) => ({
      title: `Updated ${count} sections`,
    }),
    NO_SECTIONS_FOUND: {
      title: 'No additional sections found in chapter breakdown',
    },
    DRAFT_SAVED: (count: number) => ({
      title: `Draft saved to ${count} sections`,
    }),
    REVISION_SAVED: (count: number) => ({
      title: `Revision applied to ${count} sections`,
    }),
    NO_SECTIONS_UPDATED: {
      title: 'No sections were updated',
      description: 'Revision content may not match existing sections.',
    },
    DRAFT_COMPLETE: (totalWords: number, chapterCount: number) => ({
      title: 'Draft complete!',
      description: `Generated ${totalWords.toLocaleString()} words across ${chapterCount} chapters.`,
    }),
    CHAPTER_DRAFT_ERROR: {
      title: 'Failed to generate chapter drafts',
    },
  },
} as const;

export type ToastKey = keyof typeof TOAST;
